import { InputParameterHierarchyType } from '../../../../../components/InputParameterHierarchy/model'
import { InputParametersHierarchyLevel } from '../../../../../models/InputParametersHierarchy'
import { type TemplateLookup } from '../../../../../models/TemplateLibrary'
import {
  type InputParametersHierarchyNode,
  useCreateNodeMutation,
  useDeleteNodeMutation,
  useListAccountNodesQuery,
  useUpdateNodeMutation,
} from '../../../../../store/ai/inputParametersHierarchy'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { schedulingApi } from '../../../../../store/scheduling/scheduling'
import { isReminderEmail } from '../../../../Scheduling/shared/schedulingEmailCatalog'
import { type SchedulingEmail } from '../../../Templates/SchedulingEmails/schedulingEmails'
import { useSchedulingEmails } from '../../../Templates/SchedulingEmails/useSchedulingEmails'

export const isSetupEmail = (email: SchedulingEmail): boolean => !isReminderEmail(email)

const TYPE = InputParameterHierarchyType.SCHEDULING_EMAIL_TEMPLATE

export interface SchedulingEmailChoice {
  email: SchedulingEmail
  node?: InputParametersHierarchyNode
  template?: TemplateLookup
}

export const useSchedulingEmailChoices = () => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const nodesQuery = useListAccountNodesQuery({ accountId, type: TYPE, level: InputParametersHierarchyLevel.GLOBAL, effective: true })
  const schedulingEmails = useSchedulingEmails({ refetchOnMountOrArgChange: 60 })
  const [createNode] = useCreateNodeMutation()
  const [updateNode] = useUpdateNodeMutation()
  const [deleteNode] = useDeleteNodeMutation()

  const nodes = nodesQuery.data ?? {}
  const templates = schedulingEmails.templates ?? []

  const choices: SchedulingEmailChoice[] = schedulingEmails.emails.filter(isSetupEmail).map((email) => {
    const node = nodes[email.slug]
    const templateId = node ? node.templateId : email.defaultTemplateId
    return { email, node, template: templates.find((candidate) => candidate.id === templateId) }
  })

  const select = async (email: SchedulingEmail, template: TemplateLookup) => {
    const node = nodes[email.slug]
    const request = { accountId, type: TYPE, typeId: email.slug }
    if (template.scope === 'system') {
      if (node) {
        await deleteNode({ ...request, nodeId: node.id }).unwrap()
      }
    } else if (node) {
      await updateNode({ ...request, nodeId: node.id, node: { templateId: template.id, active: true } }).unwrap()
    } else {
      await createNode({ ...request, node: { hierarchyId: { level: InputParametersHierarchyLevel.GLOBAL }, templateId: template.id, active: true } }).unwrap()
    }
    dispatch(schedulingApi.util.invalidateTags(['SchedulingEmailTemplate']))
  }

  return {
    choices,
    isLoading: nodesQuery.isLoading || schedulingEmails.isLoading,
    isError: nodesQuery.isError || schedulingEmails.isError,
    select,
  }
}
